export interface ConnectorDraftPreviewField {
  type: 'string' | 'long_text' | 'email_list' | 'datetime' | 'duration';
  value: unknown;
}

export interface ConnectorDraftPreview {
  summary: string;
  from_account?: string;
  editable_fields: Record<string, ConnectorDraftPreviewField>;
}

export interface ConnectorDraft {
  draft_id: string;
  provider: string;
  action: string;
  tool_call_id: string;
  preview: ConnectorDraftPreview;
  status?: 'pending' | 'editing' | 'sending' | 'sent' | 'rejected' | 'expired';
}

export interface ToolCall {
  role: 'user' | 'tool' | 'system' | 'assistant'
  content: string | null
  tool_call_id: string
  tool_name: string
  tool_args: Record<string, string>
  tool_call_error: boolean
  metrics: {
    time: number
  }
  created_at: number
  // Enhanced status tracking
  status?: 'pending' | 'running' | 'complete' | 'error' | 'cancelled'
  progress?: number // 0-100 percentage
  status_message?: string // Human-readable status message
  estimated_time_remaining?: number // in milliseconds
  result?: any // Tool execution result (can be any type - object, string, etc.)
  connector_draft?: ConnectorDraft // new — surfaced when a propose_* tool returns a draft
}

export interface ReasoningSteps {
  title: string
  action?: string
  result: string
  reasoning: string
  confidence?: number
  next_action?: string
}
export interface ReasoningStepProps {
  index: number
  stepTitle: string
}
export interface ReasoningProps {
  reasoning: ReasoningSteps[]
}

export type ToolCallProps = {
  tools: ToolCall
};
interface ModelMessage {
  content: string | null
  context?: MessageContext[]
  created_at: number
  metrics?: {
    time: number
    prompt_tokens: number
    input_tokens: number
    completion_tokens: number
    output_tokens: number
  }
  name: string | null
  role: string
  tool_args?: unknown
  tool_call_id: string | null
  tool_calls: Array<{
    function: {
      arguments: string
      name: string
    }
    id: string
    type: string
  }> | null
}

export interface Model {
  name: string
  model: string
  provider: string
}

export interface Agent {
  agent_id: string
  name: string
  description: string
  model: Model
  storage?: boolean
}

export interface Team {
  team_id: string
  name: string
  description: string
  model: Model
  storage?: boolean
}

interface MessageContext {
  query: string
  docs?: Array<Record<string, object>>
  time?: number
}

export enum RunEvent {
  // New skills-based events
  RunStartedEvent = 'run_started',
  FirstToken = 'first_token',
  RunCompletedEvent = 'run_completed',
  ErrorEvent = 'error',

  // Legacy events (keep for backward compatibility)
  RunStarted = 'RunStarted',
  RunContent = 'RunContent',
  RunCompleted = 'RunCompleted',
  RunError = 'RunError',
  ToolCallStarted = 'ToolCallStarted',
  ToolCallCompleted = 'ToolCallCompleted',
  ReasoningStarted = 'ReasoningStarted',
  ReasoningStep = 'ReasoningStep',
  ReasoningCompleted = 'ReasoningCompleted',
  UpdatingMemory = 'UpdatingMemory',
  TeamRunStarted = 'TeamRunStarted',
  TeamRunContent = 'TeamRunContent',
  TeamRunCompleted = 'TeamRunCompleted',
  TeamRunError = 'TeamRunError',
  TeamRunCancelled = 'TeamRunCancelled',
  TeamToolCallStarted = 'TeamToolCallStarted',
  TeamToolCallCompleted = 'TeamToolCallCompleted',
  TeamReasoningStarted = 'TeamReasoningStarted',
  TeamReasoningStep = 'TeamReasoningStep',
  TeamReasoningCompleted = 'TeamReasoningCompleted',
  TeamMemoryUpdateStarted = 'TeamMemoryUpdateStarted',
  TeamMemoryUpdateCompleted = 'TeamMemoryUpdateCompleted',
  TeamParserModelResponseStarted = 'TeamParserModelResponseStarted',
  TeamParserModelResponseCompleted = 'TeamParserModelResponseCompleted',
  TeamOutputModelResponseStarted = 'TeamOutputModelResponseStarted',
  TeamOutputModelResponseCompleted = 'TeamOutputModelResponseCompleted'
}

export interface ResponseAudio {
  id?: string
  content?: string
  transcript?: string
  channels?: number
  sample_rate?: number
}

export interface NewRunResponse {
  status: 'RUNNING' | 'PAUSED' | 'CANCELLED'
}

export interface RunResponseContent {
  content?: string | object
  content_type: string
  context?: MessageContext[]
  event: RunEvent
  event_data?: object
  messages?: ModelMessage[]
  metrics?: object
  model?: string
  run_id?: string
  agent_id?: string
  session_id?: string
  tool?: ToolCall
  tools?: Array<ToolCall>
  created_at: number
  extra_data?: AgentExtraData
  images?: ImageData[]
  videos?: VideoData[]
  audio?: AudioData[]
  response_audio?: ResponseAudio
}

export interface RunResponse {
  content?: string | object
  content_type: string
  context?: MessageContext[]
  event: RunEvent
  event_data?: object
  messages?: ModelMessage[]
  metrics?: object
  model?: string
  run_id?: string
  agent_id?: string
  agent_name?: string // Add agent_name for RunStarted events
  agent?: string // New format: agent identifier
  session_id?: string
  team_id?: string
  team_name?: string
  tool?: ToolCall
  tools?: Array<ToolCall>
  created_at: number
  extra_data?: AgentExtraData
  images?: ImageData[]
  videos?: VideoData[]
  audio?: AudioData[]
  response_audio?: ResponseAudio
  // New skills-based endpoint fields
  elapsed_ms?: number // Time elapsed since request started
  thinking?: string // Model reasoning text
  error?: string // Error message for error events
}

export interface AgentExtraData {
  reasoning_steps?: ReasoningSteps[]
  reasoning_messages?: ReasoningMessage[]
  references?: ReferenceData[]
  skills_used?: any[]
  brain_response?: any // HyperEngage Brain Response data
  member_response?: any // Team member response data (specialized formats)
  agent_id?: string // Agent context information
  agent_name?: string // Agent context information
}

export interface ReasoningMessage {
  role: 'user' | 'tool' | 'system' | 'assistant'
  content: string | null
  tool_call_id?: string
  tool_name?: string
  tool_args?: Record<string, string>
  tool_call_error?: boolean
  metrics?: {
    time: number
  }
  created_at?: number
}
export interface MessageConnectorDrafts {
  // array-form; legacy `connectorDraft` singleton kept for a grace period
  connectorDrafts?: ConnectorDraft[];
}

export interface ChatMessage extends MessageConnectorDrafts {
  role: 'user' | 'agent' | 'system' | 'tool'
  content: string
  streamingError?: boolean
  isThinking?: boolean // Add thinking state for loading indicators
  thinking?: string // Model reasoning text from skills-based endpoint
  elapsedMs?: number // Time elapsed since request started (for progress indicators)
  created_at: number
  tool_calls?: ToolCall[]
  connectorDraft?: ConnectorDraft // surfaced when a propose_* tool emits a connector_draft_ready event (legacy singleton — kept for back-compat)
  connectorDrafts?: ConnectorDraft[] // array form — multiple propose_* tools in one turn
  // Member responses emitted by team members (sub-agents)
  member_responses?: MemberResponse[]
  extra_data?: {
    reasoning_steps?: ReasoningSteps[]
    reasoning_messages?: ReasoningMessage[]
    references?: ReferenceData[]
    skills_used?: any[]
  }
  images?: ImageData[]
  videos?: VideoData[]
  audio?: AudioData[]
  response_audio?: ResponseAudio
}

// Specialized content produced by team members (sub-agents), parsed separately from main stream
export interface MemberResponse {
  agent_id?: string
  agent_name?: string
  response_type?: string
  data?: any
  timestamp?: number
  raw?: any
}

export interface AgentDetails {
  id: string
  name?: string
  db_id?: string
  // Model
  model?: Model
}

export interface TeamDetails {
  id: string
  name?: string
  db_id?: string

  // Model
  model?: Model
}

export interface ImageData {
  revised_prompt: string
  url: string
}

export interface VideoData {
  id: number
  eta: number
  url: string
}

export interface AudioData {
  base64_audio?: string
  mime_type?: string
  url?: string
  id?: string
  content?: string
  channels?: number
  sample_rate?: number
}

export interface ReferenceData {
  query: string
  references: Reference[]
  time?: number
}

export interface Reference {
  content: string
  meta_data: {
    chunk: number
    chunk_size: number
  }
  name: string
}

export interface SessionEntry {
  session_id: string
  session_name: string
  created_at: number | string
  updated_at?: number | string
  session_state?: unknown
  agent_id?: string
}

export interface Pagination {
  page: number
  limit: number
  total_pages: number
  total_count: number
}

export interface Sessions extends SessionEntry {
  data: SessionEntry[]
  meta: Pagination
}

export interface ChatEntry {
  message: {
    role: 'user' | 'system' | 'tool' | 'assistant'
    content: string
    created_at: number
  }
  response: {
    content: string
    tools?: ToolCall[]
    extra_data?: {
      reasoning_steps?: ReasoningSteps[]
      reasoning_messages?: ReasoningMessage[]
      references?: ReferenceData[]
      skills_used?: any[]
    }
    images?: ImageData[]
    videos?: VideoData[]
    audio?: AudioData[]
    response_audio?: {
      transcript?: string
    }
    created_at: number
  }
}
